import React, { useState, useEffect } from 'react'
import {
  Form,
  Input,
  Button,
  Table,
  Space,
  message,
  Popconfirm,
  Row,
  Col,
  Modal,
  Select,
} from 'antd'
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  EyeOutlined,
} from '@ant-design/icons'
import AdminHeader from '../../CommonComponents/AdminHeader'
import AdminFooter from '../../CommonComponents/AdminFooter'
import {
  getCast,
  saveCast,
  deleteCast,
  updateCast,
  getReligion,
} from '../../APIComponents/AdminApi'
import { toast } from 'react-toastify'
import axiosInstance from '../../../Utils/axiosInstance'

const CastMaster = () => {
  //===============================================
  const loginType = sessionStorage.getItem('loginType') // 'ADMIN' or 'USER'
  const menuData = JSON.parse(sessionStorage.getItem('menus')) || []

  const getPagePermission = () => {
    if (loginType === 'ADMIN') {
      return {
        is_new: 1,
        is_edit: 1,
        is_view: 1,
        is_delete: 1,
      }
    }

    for (const menu of menuData) {
      const page = menu.pages?.find((p) => p.page_name === 'Cast Master')
      if (page) return page
    }

    return {
      is_new: 0,
      is_edit: 0,
      is_view: 0,
      is_delete: 0,
    }
  }

  const pagePermission = getPagePermission()
  const showActionColumn =
    loginType === 'ADMIN' ||
    pagePermission.is_edit == 1 ||
    pagePermission.is_view == 1 ||
    pagePermission.is_delete == 1
  //============================================

  const [form] = Form.useForm()
  const [cast, setCast] = useState([])
  const [loading, setLoading] = useState(false)
  const [addLoading, setAddLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [deleteLoadingId, setDeleteLoadingId] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedDeleteId, setSelectedDeleteId] = useState(null)
  const [selectedCastName, setSelectedCastName] = useState('')
  const [religions, setReligions] = useState([])

  const [viewModalVisible, setViewModalVisible] = useState(false)
  const [selectedCast, setSelectedCast] = useState(null)

  const handleView = (record) => {
    setSelectedCast(record)
    setViewModalVisible(true)
  }

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
  })

  const fetchReligions = async () => {
    try {
      const res = await axiosInstance.get(getReligion)
      setReligions(res.data)
    } catch (err) {
      toast.error('Failed to fetch religions')
    }
  }

  //  Fetch All cast
  const fetchCast = async () => {
    try {
      setLoading(true)
      const res = await axiosInstance.get(getCast)
      setCast(res.data)
    } catch (err) {
      toast.error('Failed to fetch cast')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCast()
    fetchReligions()
  }, [])

  //  Add or Update cast
  const onFinish = async (values) => {
    const { castName, religionId } = values
    if (!castName || !religionId) {
      toast.error('Please fill all required fields')
      return
    }

    const objData = {
      cast_name: castName,
      religion_id: religionId,
    }

    try {
      if (editingId) {
        setUpdateLoading(true)
        const res = await axiosInstance.put(
          `${updateCast}/${editingId}`,
          objData,
        )
        if (res.data == 'UPDATED') {
          toast.success('Cast updated successfully')
          fetchCast()
        }
      } else {
        setAddLoading(true)
        const res = await axiosInstance.post(saveCast, objData)
        if (res.data == 'SAVED') {
          toast.success('Cast added successfully')
          fetchCast()
        }
      }
      form.resetFields()
      setEditingId(null)
    } catch (err) {
      toast.error('Operation failed. Please try again.')
    } finally {
      setAddLoading(false)
      setUpdateLoading(false)
    }
  }

  //  Edit Cast
  const handleEdit = (record) => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
    setEditingId(record.id)
    form.setFieldsValue({
      castName: record.cast_name,
      religionId: record.religion_id,
    })
  }

  //  Delete Cast
  const handleDelete = async () => {
    try {
      setDeleteLoadingId(selectedDeleteId)

      const res = await axiosInstance.delete(
        `${deleteCast}/${selectedDeleteId}`,
      )

      if (res.data === 'DELETED') {
        toast.success('Cast deleted successfully')
        fetchCast()
        setIsDeleteModalOpen(false)
        setSelectedDeleteId(null)
        setSelectedCastName('')
      }
    } catch (err) {
      toast.error('Failed to delete Cast')
    } finally {
      setDeleteLoadingId(null)
    }
  }

  //  Cancel Edit
  const handleCancel = () => {
    setEditingId(null)
    form.resetFields()
  }

  //  Table Columns
  const columns = [
    {
      title: 'Sr. No',
      key: 'index',
      render: (_, __, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
      width: '10%',
    },
    {
      title: 'Religion Name',
      dataIndex: ['religion', 'religion_name'],
    },
    {
      title: 'Cast Name',
      dataIndex: 'cast_name',
      key: 'cast_name',
    },
    ...(showActionColumn
      ? [
          {
            title: 'Actions',
            key: 'actions',
            width: '25%',
            render: (_, record) => (
              <Space>
                {pagePermission.is_view == 1 && (
                  <Button
                    icon={<EyeOutlined />}
                    onClick={() => handleView(record)}
                  />
                )}
                {pagePermission.is_edit == 1 && (
                  <Button
                    icon={<EditOutlined />}
                    title="Edit"
                    onClick={() => handleEdit(record)}
                  ></Button>
                )}
                {pagePermission.is_delete == 1 && (
                  <Button
                    title="Delete"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      setSelectedDeleteId(record.id)
                      setSelectedCastName(record.cast_name)
                      setIsDeleteModalOpen(true)
                    }}
                  ></Button>
                )}
              </Space>
            ),
          },
        ]
      : []),
  ]

  return (
    <>
      <AdminHeader />

      <div
        className="student-form-section container-fluid"
        style={{ minHeight: '100vh' }}
      >
        <div className="student-form-wrapper pb-5">
          <div className="row student-form-title">
            <h3 className="section-title1 text-start mb-2">Cast Master</h3>
          </div>

          <div className="row student-form-container shadow p-4 rounded bg-white">
            {/* Cast Form */}
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              style={{ marginBottom: '20px' }}
            >
              <Row gutter={16} className="pt-3">
                {/* Religion Dropdown */}
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label="Select Religion"
                    name="religionId"
                    rules={[
                      { required: true, message: 'Please select religion' },
                    ]}
                  >
                    <Select placeholder="Select Religion">
                      {religions.map((item) => (
                        <Select.Option key={item.id} value={item.id}>
                          {item.religion_name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                {/* Cast Name Input */}
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label="Cast Name"
                    name="castName"
                    rules={[
                      { required: true, message: 'Please enter Cast name' },
                    ]}
                  >
                    <Input placeholder="Enter Cast" />
                  </Form.Item>
                </Col>

                {/* Buttons */}
                <Col
                  xs={24}
                  sm={12}
                  md={8}
                  style={{ display: 'flex', alignItems: 'end' }}
                >
                  <Form.Item>
                    <Space>
                      {!editingId ? (
                        <>
                          {pagePermission.is_new == 1 && (
                            <Button
                              type="primary"
                              htmlType="submit"
                              icon={<PlusOutlined />}
                              loading={addLoading}
                            >
                              Add
                            </Button>
                          )}
                        </>
                      ) : (
                        <>
                          <Button
                            type="primary"
                            htmlType="submit"
                            icon={<EditOutlined />}
                            loading={updateLoading}
                          >
                            Update
                          </Button>
                          <Button
                            onClick={handleCancel}
                            disabled={updateLoading}
                          >
                            Cancel
                          </Button>
                        </>
                      )}
                    </Space>
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            {/* Cast Table */}
            <Table
              columns={columns}
              dataSource={cast}
              rowKey="id"
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: cast.length,
                onChange: (page, pageSize) => {
                  setPagination({
                    current: page,
                    pageSize: pageSize,
                  })
                },
              }}
              bordered
              loading={loading}
              className="shadow rounded custom-table p-0"
              style={{ overflowX: 'auto', border: '1px solid #dfdfdf' }}
            />
          </div>

          {/* ======== Delete Modal ====== */}
          <Modal
            title="Delete Cast"
            open={isDeleteModalOpen}
            onCancel={() => {
              setIsDeleteModalOpen(false)
              setSelectedDeleteId(null)
              setSelectedCastName('')
            }}
            footer={[
              <Button
                key="cancel"
                onClick={() => {
                  setIsDeleteModalOpen(false)
                  setSelectedDeleteId(null)
                  setSelectedCastName('')
                }}
              >
                Cancel
              </Button>,
              <Button
                key="delete"
                type="primary"
                danger
                loading={deleteLoadingId !== null}
                onClick={handleDelete}
              >
                Delete
              </Button>,
            ]}
          >
            <p>Are you sure you want to delete Cast?</p>
          </Modal>

          {/* ======== View Modal ====== */}
          <Modal
            title={
              <div style={{ fontSize: '18px', fontWeight: 600 }}>
                👁 View Cast Details
              </div>
            }
            open={viewModalVisible}
            onCancel={() => setViewModalVisible(false)}
            footer={[
              <Button
                key="close"
                type="primary"
                onClick={() => setViewModalVisible(false)}
              >
                Close
              </Button>,
            ]}
            centered
          >
            <div
              style={{
                padding: '15px',
                borderRadius: '8px',
                backgroundColor: '#f5f7fa',
                border: '1px solid #e6e6e6',
              }}
            >
              <Row gutter={[16, 16]}>
                {/* Religion Name */}
                <Col span={8}>
                  <span style={{ fontWeight: 600 }}>Religion :</span>
                </Col>

                <Col span={16}>
                  <div
                    style={{
                      backgroundColor: '#ffffff',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #d9d9d9',
                    }}
                  >
                    {selectedCast?.religion?.religion_name}
                  </div>
                </Col>

                {/* Cast Name */}
                <Col span={8}>
                  <span style={{ fontWeight: 600 }}>Cast Name :</span>
                </Col>

                <Col span={16}>
                  <div
                    style={{
                      backgroundColor: '#ffffff',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #d9d9d9',
                    }}
                  >
                    {selectedCast?.cast_name}
                  </div>
                </Col>
              </Row>
            </div>
          </Modal>

        </div>
      </div>

      <AdminFooter />
    </>
  )
}

export default CastMaster
